import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { createPortal } from "react-dom";

const API_URL = process.env.REACT_APP_API_URL;

function extractExcelBase64(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t.startsWith("UEs")) return raw;
    if (t.startsWith("{")) {
      try {
        return extractExcelBase64(JSON.parse(t));
      } catch {
        return null;
      }
    }
    return raw;
  }
  if (typeof raw === "object") {
    if (raw.body != null) return extractExcelBase64(raw.body);
    if (typeof raw.data === "string") return extractExcelBase64(raw.data);
  }
  return null;
}

function hasMeaningfulQuantity(value) {
  const str = value?.toString().trim();
  if (!str) return false;
  const num = parseFloat(str);
  return Number.isFinite(num) && num > 0;
}

function parseQuantity(value) {
  const num = parseFloat(String(value ?? "").trim());
  return Number.isFinite(num) && num > 0 ? num : 0;
}

function isEmptyProcurementRecord(record) {
  const code = String(record?.materialCode || "").trim();
  const desc = String(record?.materialDescription || "").trim();
  const hsn = String(record?.hsnCode || "").trim();
  const uom = String(record?.uom || "").trim();
  return (
    !code && !desc && !hsn && !uom && !hasMeaningfulQuantity(record?.quantity)
  );
}

function getUserRefId(ref) {
  if (!ref) return ''
  if (typeof ref === 'object') {
    return String(ref._id || ref.id || '').trim()
  }
  return String(ref).trim()
}

function canRequestUserUpdateRecord(record, requestUserId, role) {
  const normalizedRole = String(role || '').toLowerCase()
  if (normalizedRole === 'admin' || normalizedRole === 'supervisor') {
    return true
  }

  const requesterId = String(requestUserId || '').trim()
  if (!requesterId) return false

  const createdById = getUserRefId(record?.createdBy)
  const ownerId = getUserRefId(record?.userId)
  // If ownership fields are missing, allow update so draft shells still work
  if (!createdById && !ownerId) return true

  return createdById === requesterId || ownerId === requesterId
}

const BOQ_KEY_SEP = "__boq__";

const makeBoqSearchKey = (parentId, boqId) =>
  `${parentId}${BOQ_KEY_SEP}${boqId}`;

const parseBoqSearchKey = (key) => {
  const raw = String(key || "");
  const idx = raw.indexOf(BOQ_KEY_SEP);
  if (idx <= 0) return null;
  return {
    parentId: raw.slice(0, idx),
    boqId: raw.slice(idx + BOQ_KEY_SEP.length),
  };
};

const createEmptyBoqItem = () => ({
  id: `boq-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  materialCode: "",
  materialDescription: "",
  hsnCode: "",
  uom: "",
  quantity: "",
});

const mapBoqItemsFromRecord = (boqList) => {
  if (!Array.isArray(boqList)) return [];
  return boqList.map((item, i) => ({
    id: `boq-loaded-${i}-${String(item?.materialCode || i)}`,
    materialCode: item?.materialCode || "",
    materialDescription: item?.materialDescription || "",
    hsnCode: item?.hsnCode || "",
    uom: item?.uom || "",
    quantity: hasMeaningfulQuantity(item?.quantity)
      ? String(item.quantity)
      : "",
  }));
};

const isBoqItemEmpty = (item) => {
  const code = String(item?.materialCode || "").trim();
  const desc = String(item?.materialDescription || "").trim();
  const hsn = String(item?.hsnCode || "").trim();
  const uom = String(item?.uom || "").trim();
  return (
    !code && !desc && !hsn && !uom && !hasMeaningfulQuantity(item?.quantity)
  );
};

const isBoqItemFullyFilled = (item) => {
  const code = String(item?.materialCode || "").trim();
  const desc = String(item?.materialDescription || "").trim();
  const hsn = String(item?.hsnCode || "").trim();
  const uom = String(item?.uom || "").trim();
  return (
    !!code &&
    !!desc &&
    !!hsn &&
    !!uom &&
    hasMeaningfulQuantity(item?.quantity)
  );
};

const buildBoqPayloadItems = (row) => {
  if (!row?.isBoq) return [];
  return (row.boq || [])
    .filter((item) => !isBoqItemEmpty(item))
    .map((item) => ({
      materialCode: String(item.materialCode || "").trim(),
      materialDescription: String(item.materialDescription || "").trim(),
      hsnCode: String(item.hsnCode || "").trim(),
      uom: String(item.uom || "").trim(),
      quantity: parseQuantity(item.quantity),
    }));
};

const buildRowSnapshotFields = (row) => ({
  materialCode: row.materialCode || "",
  materialDescription: row.materialDescription || "",
  hsnCode: row.hsnCode || "",
  uom: row.uom || "",
  quantity: row.quantity || "",
  userId: row.userId || "",
  isBoq: !!row.isBoq,
  boq: (row.boq || []).map((item) => ({
    materialCode: item.materialCode || "",
    materialDescription: item.materialDescription || "",
    hsnCode: item.hsnCode || "",
    uom: item.uom || "",
    quantity: item.quantity || "",
  })),
});

function toDateInputValue(value) {
  if (!value) return "";
  const str = String(value).trim();
  // HTML date input needs YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  // API often returns DD-MM-YYYY or DD/MM/YYYY (e.g. "26-10-2026")
  const dmy = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dmy) {
    const day = dmy[1].padStart(2, "0");
    const month = dmy[2].padStart(2, "0");
    const year = dmy[3];
    return `${year}-${month}-${day}`;
  }
  const date = new Date(str);
  if (Number.isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const buildFormSnapshot = (common, rows) =>
  JSON.stringify({
    common: {
      prNo: common?.prNo || "",
      clientReferenceId: common?.clientReferenceId || "",
      clientReference: common?.clientReference || "",
      deliveryLocation: common?.deliveryLocation || "",
      prSummary: common?.prSummary || "",
      dueDate: common?.dueDate || "",
    },
    rows: (rows || []).map(buildRowSnapshotFields),
  });

const ProcurementTrackerAdd = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [users, setUsers] = useState([]);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [currentUserId, setCurrentUserId] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isDownloadingSample, setIsDownloadingSample] = useState(false);
  const fileInputRef = useRef(null);
  const [importResults, setImportResults] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomerIndex, setSelectedCustomerIndex] = useState(-1);
  const [isCustomersLoading, setIsCustomersLoading] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState("");
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(-1);
  const [isFetchExistingMode, setIsFetchExistingMode] = useState(false);
  const [isDraftMode, setIsDraftMode] = useState(false);
  const [isNewDraftFromAdd, setIsNewDraftFromAdd] = useState(false);
  const [emptyDraftRecordIds, setEmptyDraftRecordIds] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [materialSearchTerms, setMaterialSearchTerms] = useState({});
  const [showMaterialDropdown, setShowMaterialDropdown] = useState({});
  const [selectedMaterialIndex, setSelectedMaterialIndex] = useState({});
  const materialInputRefs = useRef({});
  const [materialDropdownPos, setMaterialDropdownPos] = useState({});
  const [duplicateMaterialRowId, setDuplicateMaterialRowId] = useState(null);
  const [isMaterialsLoading, setIsMaterialsLoading] = useState(false);
  const [unitOptions, setUnitOptions] = useState([]);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const materialDescInputRefs = useRef({});

const [materialDescriptionSearchTerms, setMaterialDescriptionSearchTerms] =
  useState({});

const [showMaterialDescriptionDropdown, setShowMaterialDescriptionDropdown] =
  useState({});

const [selectedMaterialDescriptionIndex, setSelectedMaterialDescriptionIndex] =
  useState({});

const [materialDescriptionDropdownPos, setMaterialDescriptionDropdownPos] =
  useState({});

  // Common fields for all rows
  const [commonData, setCommonData] = useState({
    prNo: "",
    clientReferenceId: "",
    clientReference: "",
    deliveryLocation: "",
    prSummary: "",
    dueDate: "",
  });

  // Array of procurement rows
  const [procurementRows, setProcurementRows] = useState([]);

  const [initialSnapshot, setInitialSnapshot] = useState(null);

  const isDirty = useMemo(() => {
    if (initialSnapshot === null) return false;
    return buildFormSnapshot(commonData, procurementRows) !== initialSnapshot;
  }, [commonData, procurementRows, initialSnapshot]);

  const mapProcurementRecordToRow = (record, index, asExisting) => ({
    id: index + 1,
    materialCode: record.materialCode || "",
    materialDescription: record.materialDescription || "",
    hsnCode: record.hsnCode || "",
    uom: record.uom || "",
    quantity: hasMeaningfulQuantity(record.quantity)
      ? String(record.quantity)
      : "",
    userId: record.userId?._id || record.userId || "",
    isExisting: asExisting,
    existingId: record._id,
    originalRecord: record,
    isBoq: !!record.isBoq,
    boq: mapBoqItemsFromRecord(record.boq),
    lineNo: record.lineNo || index + 1,
  });

  const recordsToEditableRows = (records) =>
    records
      .filter((record) => !isEmptyProcurementRecord(record))
      .map((record, index) => mapProcurementRecordToRow(record, index, true));

  const buildCommonDataFromRecord = (record, prNo) => ({
    prNo: record.prNo || prNo,
    clientReference: record.clientReference || "",
    deliveryLocation: record.deliveryLocation || "",
    clientReferenceId: record.clientReferenceId || "",
    prSummary: record.prSummary || "",
    dueDate: toDateInputValue(record.dueDate),
  });

  const collectEmptyDraftIdsForUser = (records, currentId, role) =>
    (records || [])
      .filter(isEmptyProcurementRecord)
      .filter((record) =>
        canRequestUserUpdateRecord(record, currentId, role),
      )
      .map((record) => record._id)
      .filter(Boolean);

  const applyLoadedProcurementRecords = (records, prNo, { loadRows = false, currentId = "", role = "" } = {}) => {
    if (!Array.isArray(records) || records.length === 0) {
      setCommonData((prev) => {
        const updated = { ...prev, prNo };
        setInitialSnapshot(buildFormSnapshot(updated, []));
        return updated;
      });
      setProcurementRows([]);
      setEmptyDraftRecordIds([]);
      return;
    }

    const builtCommon = buildCommonDataFromRecord(records[0], prNo);
    setCommonData(builtCommon);
    setEmptyDraftRecordIds(
      collectEmptyDraftIdsForUser(
        records,
        currentId || currentUserId,
        role || currentUserRole,
      ),
    );

    const rowsForSnapshot = loadRows ? recordsToEditableRows(records) : [];
    if (loadRows) {
      setProcurementRows(rowsForSnapshot);
    } else {
      setProcurementRows([]);
    }
    setInitialSnapshot(buildFormSnapshot(builtCommon, rowsForSnapshot));
  };

  useEffect(() => {
    fetchCustomers();
    fetchUnits();
  }, []);

  const filteredCustomers = (customers || []).filter((c) =>
    c.customerName?.toLowerCase().includes(customerSearchTerm.toLowerCase()),
  );

  const fetchUnits = async () => {
    setIsLoadingUnits(true);
    try {
      const res = await axios.post(API_URL, {
        action: "get-all-units",
        page: 1,
        limit: 500,
        sortBy: "unit",
        sortOrder: "asc",
      });

      let data = res.data;
      if (typeof data === "string") {
        data = JSON.parse(data);
      }

      const list = Array.isArray(data?.data?.units) ? data.data.units : [];
      const activeUnits = list
        .map((item) => ({
          id: item?._id || item?.id || "",
          unit: String(item?.unit || "").trim(),
          status: String(item?.status || "").trim(),
        }))
        .filter((item) => item.unit)
        .filter(
          (item) => !item.status || item.status.toLowerCase() === "active",
        );

      setUnitOptions(data?.success ? activeUnits : []);
    } catch (err) {
      console.error("Fetch units error:", err);
      setUnitOptions([]);
    } finally {
      setIsLoadingUnits(false);
    }
  };

  // Customer / user list for form pickers only — does NOT require customer_master
  // or user_management moduleAccess (see utils/lookupAccess.js).
  const fetchCustomers = async () => {
    setIsCustomersLoading(true);
    try {
      const res = await axios.post(API_URL, {
        action: "get-all-customers",
        page: 1,
        limit: 5000,
      });

      let data = res.data;

      if (typeof data === "string") {
        data = JSON.parse(data);
      }

      if (data.success) {
        const list = Array.isArray(data.data)
          ? data.data
          : data.data?.customers || [];

        setCustomers(list);
      } else {
        setCustomers([]);
      }
    } catch (err) {
      console.error(err);
      setCustomers([]);
    } finally {
      setIsCustomersLoading(false);
    }
  };

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;

    setCustomerSearchTerm(value);
    setShowCustomerDropdown(true);

    if (!value) {
      setCommonData((prev) => ({
        ...prev,
        clientReferenceId: "",
        clientReference: "",
        deliveryLocation: "",
      }));
      setLocationSearchTerm("");
      setSelectedLocationIndex(-1);
    }
  };

  useEffect(() => {
    setCustomerSearchTerm(commonData.clientReference || "");
  }, [commonData.clientReference]);

  useEffect(() => {
    setLocationSearchTerm(commonData.deliveryLocation || "");
  }, [commonData.deliveryLocation]);

  const handleCustomerSelect = (customer) => {
    setCustomerSearchTerm(customer.customerName);

    setCommonData((prev) => ({
      ...prev,
      clientReferenceId: customer._id,
      clientReference: customer.customerName,
      deliveryLocation: "", // OK to reset only location
    }));

    setLocationSearchTerm("");
    setSelectedLocationIndex(-1);
    setShowCustomerDropdown(false);
  };

  const handleCustomerKeyDown = (e) => {
    if (!showCustomerDropdown) return;

    if (e.key === "ArrowDown") {
      setSelectedCustomerIndex((prev) =>
        prev < filteredCustomers.length - 1 ? prev + 1 : prev,
      );
    } else if (e.key === "ArrowUp") {
      setSelectedCustomerIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      if (selectedCustomerIndex >= 0) {
        handleCustomerSelect(filteredCustomers[selectedCustomerIndex]);
      }
    }
  };

  const handleLocationSearchChange = (e) => {
    const value = e.target.value;

    setLocationSearchTerm(value);
    setShowLocationDropdown(true);

    setCommonData((prev) => ({
      ...prev,
      deliveryLocation: value,
    }));
  };

  const handleLocationSelect = (loc) => {
    setLocationSearchTerm(loc);

    setCommonData((prev) => ({
      ...prev,
      deliveryLocation: loc,
    }));

    setShowLocationDropdown(false);
  };

  const handleLocationKeyDown = (e) => {
    if (!showLocationDropdown) return;

    if (e.key === "ArrowDown") {
      setSelectedLocationIndex((prev) =>
        prev < filteredLocations.length - 1 ? prev + 1 : prev,
      );
    } else if (e.key === "ArrowUp") {
      setSelectedLocationIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      if (selectedLocationIndex >= 0) {
        handleLocationSelect(filteredLocations[selectedLocationIndex]);
      }
    }
  };

  const selectedCustomer = customers.find(
    (c) => String(c._id) === String(commonData.clientReferenceId),
  );

  const locationOptions = React.useMemo(() => {
    const customer = customers.find(
      (c) => String(c._id) === String(commonData.clientReferenceId),
    );

    if (!customer) return [];

    const cities = (customer.contacts || []).map((c) => c.city).filter(Boolean);

    return [...new Set(cities)];
  }, [customers, commonData.clientReferenceId]);

  const filteredLocations = (locationOptions || []).filter((loc) =>
    loc.toLowerCase().includes(locationSearchTerm.toLowerCase()),
  );

  // Fetch users for dropdown and get current user role
  useEffect(() => {
    const prNoFromUrl = searchParams.get("prNo");
    const shouldFetchExisting = searchParams.get("fetchExisting") === "true";
    const shouldLoadDraft = searchParams.get("isDraft") === "true";
    const draftIdFromUrl = searchParams.get("id");

    const role = localStorage.getItem("role");
    const currentId = localStorage.getItem("id");

    if (role) {
      setCurrentUserRole(role.toLowerCase());
    }

    if (currentId) {
      setCurrentUserId(currentId);
    }

    if (prNoFromUrl && shouldLoadDraft) {
      setIsDraftMode(true);
      const isNewDraft = Boolean(draftIdFromUrl);
      setIsNewDraftFromAdd(isNewDraft);
      fetchDraftProcurementData(prNoFromUrl, draftIdFromUrl, role, currentId, {
        loadRows: !isNewDraft,
      });
    } else if (prNoFromUrl && shouldFetchExisting) {
      setIsFetchExistingMode(true);
      fetchExistingProcurementData(prNoFromUrl, role, currentId);
    } else if (prNoFromUrl) {
      setCommonData((prev) => {
        const updated = { ...prev, prNo: prNoFromUrl };
        setInitialSnapshot(buildFormSnapshot(updated, []));
        return updated;
      });
      setProcurementRows([]);
    } else {
      fetchNextPrNumber(role, currentId);
    }

    if (
      role &&
      (role.toLowerCase() === "admin" || role.toLowerCase() === "supervisor")
    ) {
      fetchUsers();
    }
    // Intentionally keyed only to URL params; fetch helpers read latest role/id from localStorage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch next PR number from API
  const fetchNextPrNumber = async (role, currentId) => {
    try {
      const payload = {
        action: "get-next-pr-number",
      };

      const response = await axios.post(API_URL, payload);
      let responseData = response.data;

      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      if (
        responseData.success === true &&
        responseData.data &&
        responseData.data.nextPrNo
      ) {
        setCommonData((prev) => {
          const updated = { ...prev, prNo: responseData.data.nextPrNo };
          setInitialSnapshot(buildFormSnapshot(updated, []));
          return updated;
        });
      } else {
        toast.error("Failed to fetch PR number", { autoClose: 2000 });
      }
    } catch (error) {
      console.error("Fetch next PR number error:", error);
      toast.error("Failed to fetch PR number", { autoClose: 2000 });
    }
  };

  const isMaterialCodeDuplicate = (rowId, materialCode) => {
    const code = String(materialCode || "")
      .trim()
      .toLowerCase();
    // "-" is allowed as a placeholder and may repeat across rows
    if (!code || code === "-") return false;

    return procurementRows.some(
      (row) =>
        String(row.id) !== String(rowId) &&
        !isRowEmpty(row) && // ignore blank rows
        String(row.materialCode || "")
          .trim()
          .toLowerCase() === code,
    );
  };

  const openMaterialDropdown = useCallback((rowId) => {
    if (isMaterialsLoading) return;
    const el = materialInputRefs.current[rowId];
    if (el) {
      const rect = el.getBoundingClientRect();
      setMaterialDropdownPos((prev) => ({
        ...prev,
        [rowId]: {
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        },
      }));
    }
    setShowMaterialDropdown((prev) => ({ ...prev, [rowId]: true }));
  }, [isMaterialsLoading]);

  useEffect(() => {
    const activeRowId = Object.keys(showMaterialDropdown).find(
      (id) => showMaterialDropdown[id],
    );
    if (!activeRowId) return;

    const reposition = () => openMaterialDropdown(activeRowId);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [showMaterialDropdown, openMaterialDropdown]);

  const fetchMaterials = async () => {
    setIsMaterialsLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-material",
        page: 1,
        limit: 5000,
      });

      let data = response.data;

      if (typeof data === "string") {
        data = JSON.parse(data);
      }

      if (data.success) {
        setMaterials(data.data?.materials || []);
      } else {
        setMaterials([]);
        console.log("Materials Loaded:", data.data?.materials);
      }
    } catch (err) {
      console.error(err);
      setMaterials([]);
    } finally {
      setIsMaterialsLoading(false);
    }
  };

  useEffect(() => {
    fetchMaterials();
  }, []);

  const getFilteredMaterials = (rowId) => {
    const search = (materialSearchTerms[rowId] || "").toLowerCase();

    const list = Array.isArray(materials) ? materials : [];

    return list.filter(
      (m) =>
        m.materialCode?.toLowerCase().includes(search) ||
        m.productName?.toLowerCase().includes(search),
    );
  };

  const handleMaterialSearchChange = (rowId, value) => {
    setMaterialSearchTerms((prev) => ({ ...prev, [rowId]: value }));
    setSelectedMaterialIndex((prev) => ({ ...prev, [rowId]: -1 }));
    openMaterialDropdown(rowId);

    const boqRef = parseBoqSearchKey(rowId);
    if (boqRef) {
      handleUpdateBoqItem(
        boqRef.parentId,
        boqRef.boqId,
        "materialCode",
        value,
      );
      return;
    }

    handleUpdateRow(rowId, "materialCode", value);

    if (isMaterialCodeDuplicate(rowId, value)) {
      setDuplicateMaterialRowId(rowId);
    } else if (duplicateMaterialRowId === rowId) {
      setDuplicateMaterialRowId(null);
    }
  };

  const handleMaterialSelect = (rowId, material) => {
    const boqRef = parseBoqSearchKey(rowId);

    if (!boqRef && isMaterialCodeDuplicate(rowId, material.materialCode)) {
      toast.error(
        `Material code "${material.materialCode}" is already added in another row`,
        { autoClose: 2500 },
      );
      return;
    }

    const fields = {
      materialCode: material.materialCode,
      materialDescription: material.productName,
      hsnCode: material.hsnCode,
      uom: material.uom,
    };

    if (boqRef) {
      handleUpdateBoqItemFields(boqRef.parentId, boqRef.boqId, fields);
      setMaterialDescriptionSearchTerms((prev) => ({
        ...prev,
        [rowId]: material.productName,
      }));
    } else {
      handleUpdateRowFields(rowId, fields);
    }

    setMaterialSearchTerms((prev) => ({
      ...prev,
      [rowId]: material.materialCode,
    }));

    setShowMaterialDropdown((prev) => ({
      ...prev,
      [rowId]: false,
    }));

    setSelectedMaterialIndex((prev) => ({
      ...prev,
      [rowId]: -1,
    }));
  };

  useEffect(() => {
    const values = {};

    procurementRows.forEach((row) => {
      values[row.id] = row.materialCode;
      (row.boq || []).forEach((item) => {
        values[makeBoqSearchKey(row.id, item.id)] = item.materialCode;
      });
    });

    setMaterialSearchTerms(values);
  }, [procurementRows]);

  const handleMaterialKeyDown = (rowId, e) => {
    if (!showMaterialDropdown[rowId]) return;

    const list = getFilteredMaterials(rowId);
    const currentIndex = selectedMaterialIndex[rowId] ?? -1;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedMaterialIndex((prev) => ({
        ...prev,
        [rowId]:
          currentIndex < list.length - 1 ? currentIndex + 1 : currentIndex,
      }));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedMaterialIndex((prev) => ({
        ...prev,
        [rowId]: currentIndex > 0 ? currentIndex - 1 : 0,
      }));
    } else if (e.key === "Enter") {
      if (currentIndex >= 0 && list[currentIndex]) {
        e.preventDefault();
        handleMaterialSelect(rowId, list[currentIndex]);
      }
    }
  };
  useEffect(() => {
  const values = {};

  procurementRows.forEach((row) => {
    values[row.id] = row.materialDescription;
    (row.boq || []).forEach((item) => {
      values[makeBoqSearchKey(row.id, item.id)] = item.materialDescription;
    });
  });

  setMaterialDescriptionSearchTerms(values);
}, [procurementRows]);

const getFilteredMaterialDescriptions = (rowId) => {
  const search = (materialDescriptionSearchTerms[rowId] || "")
    .trim()
    .toLowerCase();

  if (!search) return materials || [];

  return (materials || []).filter((m) => {
    return [
      m.productName,
      m.categoryName,
      m.subcategoryName,
      m.partNumber,
      m.makeBrandOem,
    ]
      .filter(Boolean)
      .some((value) =>
        String(value).toLowerCase().includes(search)
      );
  });
};


const handleMaterialDescriptionSearchChange = (rowId, value) => {
  setMaterialDescriptionSearchTerms((prev) => ({
    ...prev,
    [rowId]: value,
  }));

  setSelectedMaterialDescriptionIndex((prev) => ({
    ...prev,
    [rowId]: -1,
  }));

  openMaterialDescriptionDropdown(rowId);

  const boqRef = parseBoqSearchKey(rowId);
  if (boqRef) {
    handleUpdateBoqItem(
      boqRef.parentId,
      boqRef.boqId,
      "materialDescription",
      value,
    );
    return;
  }

  handleUpdateRow(rowId, "materialDescription", value);
};


const handleMaterialDescriptionSelect = (rowId, material) => {
  const boqRef = parseBoqSearchKey(rowId);

  if (!boqRef && isMaterialCodeDuplicate(rowId, material.materialCode)) {
    toast.error(
      `Material code "${material.materialCode}" is already added`
    );
    return;
  }

  const fields = {
    materialCode: material.materialCode,
    materialDescription: material.productName,
    hsnCode: material.hsnCode,
    uom: material.uom,
  };

  if (boqRef) {
    handleUpdateBoqItemFields(boqRef.parentId, boqRef.boqId, fields);
  } else {
    handleUpdateRowFields(rowId, fields);
  }

  setMaterialSearchTerms((prev) => ({
    ...prev,
    [rowId]: material.materialCode,
  }));

  setMaterialDescriptionSearchTerms((prev) => ({
    ...prev,
    [rowId]: material.productName,
  }));

  setShowMaterialDescriptionDropdown((prev) => ({
    ...prev,
    [rowId]: false,
  }));
};  

const openMaterialDescriptionDropdown = useCallback((rowId) => {
  if (isMaterialsLoading) return;

  const el = materialDescInputRefs.current[rowId];

  if (el) {
    const rect = el.getBoundingClientRect();

    setMaterialDescriptionDropdownPos((prev) => ({
      ...prev,
      [rowId]: {
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      },
    }));
  }

  setShowMaterialDescriptionDropdown((prev) => ({
    ...prev,
    [rowId]: true,
  }));
}, [isMaterialsLoading]);

const handleMaterialDescriptionKeyDown = (rowId, e) => {
  if (!showMaterialDescriptionDropdown[rowId]) return;

  const list = getFilteredMaterialDescriptions(rowId);
  const currentIndex = selectedMaterialDescriptionIndex[rowId] ?? -1;

  if (e.key === "ArrowDown") {
    e.preventDefault();
    setSelectedMaterialDescriptionIndex((prev) => ({
      ...prev,
      [rowId]:
        currentIndex < list.length - 1 ? currentIndex + 1 : currentIndex,
    }));
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    setSelectedMaterialDescriptionIndex((prev) => ({
      ...prev,
      [rowId]: currentIndex > 0 ? currentIndex - 1 : 0,
    }));
  } else if (e.key === "Enter") {
    if (currentIndex >= 0 && list[currentIndex]) {
      e.preventDefault();

      // Add this line here
      handleMaterialDescriptionSelect(rowId, list[currentIndex]);
    }
  }
};
useEffect(() => {
  const activeRowId = Object.keys(showMaterialDescriptionDropdown).find(
    (id) => showMaterialDescriptionDropdown[id]
  );

  if (!activeRowId) return;

  const reposition = () => {
    openMaterialDescriptionDropdown(activeRowId);
  };

  window.addEventListener("scroll", reposition, true);
  window.addEventListener("resize", reposition);

  return () => {
    window.removeEventListener("scroll", reposition, true);
    window.removeEventListener("resize", reposition);
  };
}, [showMaterialDescriptionDropdown, openMaterialDescriptionDropdown]);

  // Fetch draft procurement data (editable rows from Add button)
  const fetchDraftProcurementData = async (
    prNo,
    draftId,
    role,
    currentId,
    { loadRows = false } = {},
  ) => {
    setIsFetchingData(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-procurements-by-prno",
        prNo,
      });
      let responseData = response.data;

      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      if (
        responseData.success === true &&
        Array.isArray(responseData.data) &&
        responseData.data.length > 0
      ) {
        let draftRecords = responseData.data;

        if (draftId) {
          const matched = draftRecords.filter(
            (record) => String(record._id) === String(draftId),
          );
          if (matched.length > 0) {
            draftRecords = matched;
          }
        }

        applyLoadedProcurementRecords(draftRecords, prNo, {
          loadRows,
          currentId,
          role,
        });
      } else {
        applyLoadedProcurementRecords([], prNo, {
          loadRows: false,
          currentId,
          role,
        });
      }
    } catch (error) {
      console.error("Fetch draft procurement data error:", error);
      applyLoadedProcurementRecords([], prNo, {
        loadRows: false,
        currentId,
        role,
      });
      toast.error("Failed to load draft data", { autoClose: 2000 });
    } finally {
      setIsFetchingData(false);
    }
  };

  // Fetch existing procurement data for the PR number
  const fetchExistingProcurementData = async (prNo, role, currentId) => {
    setIsFetchingData(true);
    try {
      const payload = {
        action: "get-procurements-by-prno",
        prNo: prNo,
      };

      const response = await axios.post(API_URL, payload);
      let responseData = response.data;

      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      if (
        responseData.success === true &&
        responseData.data &&
        responseData.data.length > 0
      ) {
        const existingData = responseData.data;
        const builtCommon = buildCommonDataFromRecord(existingData[0], prNo);
        setCommonData(builtCommon);
        setEmptyDraftRecordIds(
          collectEmptyDraftIdsForUser(existingData, currentId, role),
        );
        const rows = existingData.map((record, index) =>
          mapProcurementRecordToRow(record, index, true),
        );
        setProcurementRows(rows);
        setInitialSnapshot(buildFormSnapshot(builtCommon, rows));
      } else {
        applyLoadedProcurementRecords([], prNo, {
          loadRows: false,
          currentId,
          role,
        });
      }
    } catch (error) {
      console.error("Fetch existing procurement data error:", error);
      applyLoadedProcurementRecords([], prNo, {
        loadRows: false,
        currentId,
        role,
      });
      toast.error("Failed to load existing data", { autoClose: 2000 });
    } finally {
      setIsFetchingData(false);
    }
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];

      if (!allowedTypes.includes(file.type)) {
        toast.error("Upload Excel only (.xlsx, .xls)");
        return;
      }

      setImportFile(file);
      setImportResults(null);
    }
  };

  const toBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
    });
  };

  const handleExcelImport = async () => {
    if (!importFile) {
      toast.error("Please select Excel file");
      return;
    }

    setIsImporting(true);

    try {
      const base64 = await toBase64(importFile);

      const payload = {
        action: "import-procurement",
        fileBase64: base64,
        userId: localStorage.getItem("id"),
        createdBy: localStorage.getItem("id"),
      };

      const res = await axios.post(API_URL, payload);

      let data = res.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          data = {};
        }
      }

      if (data.success) {
        toast.success(data.message || "Imported successfully");

        setImportResults(data.results || null);

        setImportFile(null);

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        const importedPR = data?.results?.importedRecords?.[0]?.prNo;

        if (importedPR) {
          setIsFetchExistingMode(false);
          await fetchDraftProcurementData(
            importedPR,
            null,
            currentUserRole,
            currentUserId,
            { loadRows: true },
          );
        }
      } else {
        toast.error(data.message || "Import failed");
      }
    } catch (err) {
      console.error(err);

      if (err.response && err.response.data) {
        let errorData = err.response.data;

        if (typeof errorData === "string") {
          try {
            errorData = JSON.parse(errorData);
          } catch {
            errorData = {};
          }
        }

        toast.error(errorData.message || "Import failed");
      } else {
        toast.error("Network error. Please try again.");
      }
    } finally {
      setIsImporting(false);
    }
  };
  // User assign picker — parent module is procurement_tracker; do not gate on user_management.
  const fetchUsers = async () => {
    try {
      const payload = {
        action: "get-users",
        page: 1,
        limit: 1000,
      };

      const response = await axios.post(API_URL, payload);
      let responseData = response.data;

      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      if (responseData.success === true && responseData.users) {
        setUsers(responseData.users.filter((user) => user.status === "active"));
      }
    } catch (error) {
      console.error("Fetch users error:", error);
    }
  };

  // Handle common field changes
  const handleCommonChange = (e) => {
    const { name, value } = e.target;
    setCommonData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDownloadTemplate = async () => {
    setIsDownloadingSample(true);

    try {
      const payload = {
        action: "get-procurement-download",
      };

      const response = await axios.post(API_URL, payload, {
        responseType: "json",
      });

      let base64 = extractExcelBase64(response.data);

      if (!base64 || typeof base64 !== "string") {
        throw new Error("Invalid export response");
      }

      base64 = base64.trim();

      if (!base64.startsWith("UEs")) {
        throw new Error("Invalid Excel data received");
      }

      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;

      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, -5);

      link.download = `procurement-template-${timestamp}.xlsx`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Template downloaded successfully", { autoClose: 2000 });
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download template. Please try again.", {
        autoClose: 2000,
      });
    } finally {
      setIsDownloadingSample(false);
    }
  };

  const isRowEmpty = (row) => {
    const hasMaterialCode = row.materialCode && row.materialCode.trim() !== "";
    const hasMaterialDescription =
      row.materialDescription && row.materialDescription.trim() !== "";
    const hasHsnCode = row.hsnCode && row.hsnCode.trim() !== "";
    const hasUom = row.uom && row.uom.trim() !== "";
    const hasQuantity = hasMeaningfulQuantity(row.quantity);
    return (
      !hasMaterialCode &&
      !hasMaterialDescription &&
      !hasHsnCode &&
      !hasUom &&
      !hasQuantity
    );
  };

  const isRowFilledForSubmit = (row) => {
    const hasMaterialCode = row.materialCode && row.materialCode.trim() !== "";
    const hasMaterialDescription =
      row.materialDescription && row.materialDescription.trim() !== "";
    const hasHsnCode = row.hsnCode && row.hsnCode.trim() !== "";
    const hasUom = row.uom && row.uom.trim() !== "";
    const hasQuantity = hasMeaningfulQuantity(row.quantity);
    const hasUserId = row.userId && row.userId.trim() !== "";
    return (
      hasMaterialCode ||
      hasMaterialDescription ||
      hasHsnCode ||
      hasUom ||
      hasQuantity ||
      ((currentUserRole === "admin" || currentUserRole === "supervisor") &&
        hasUserId)
    );
  };

  const isRowFullyFilled = (row) => {
    const hasMaterialCode = row.materialCode && row.materialCode.trim() !== "";
    const hasMaterialDescription =
      row.materialDescription && row.materialDescription.trim() !== "";
    const hasHsnCode = row.hsnCode && row.hsnCode.trim() !== "";
    const hasUom = row.uom && row.uom.trim() !== "";
    const hasQuantity = hasMeaningfulQuantity(row.quantity);
    const hasUserId = row.userId && row.userId.trim() !== "";
    const baseFields =
      hasMaterialCode &&
      hasMaterialDescription &&
      hasHsnCode &&
      hasUom &&
      hasQuantity;

    if (currentUserRole === "admin" || currentUserRole === "supervisor") {
      return baseFields && hasUserId;
    }
    return baseFields;
  };

  const getSupplierReferencesFromRecord = (record) => {
    if (!record?.supplierReference) return [];
    if (Array.isArray(record.supplierReference)) {
      return record.supplierReference.filter(Boolean);
    }
    return String(record.supplierReference)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const getSupplierIdsFromRecord = (record) => {
    const raw = record?.supplierIds ?? record?.supplierId;
    if (!raw) return [];
    const arr = Array.isArray(raw) ? raw : [raw];
    return arr
      .map((x) =>
        x && typeof x === "object" && x._id ? String(x._id) : String(x),
      )
      .filter(Boolean);
  };

  const getAttachmentsFromRecord = (record) => {
    const raw =
      record?.attachments ??
      record?.s3Keys ??
      (record?.s3Key ? [record.s3Key] : []);
    return Array.isArray(raw) ? raw : [];
  };

  const buildUpdatePayload = (row, loggedInUserId, lineNo) => {
    const record = row.originalRecord || {};
    const procurementId = row.existingId || record._id;
    const originalOwnerId = getUserRefId(record.userId) || getUserRefId(row.userId);
    const isBoq = !!row.isBoq;
    return {
      action: "update-procurement",
      requestUserId: loggedInUserId,
      id: procurementId,
      prNo: commonData.prNo.trim(),
      prSummary: commonData.prSummary.trim(),
      clientReference:
        selectedCustomer?.customerName || commonData.clientReference.trim(),
      clientReferenceId: commonData.clientReferenceId,
      deliveryLocation: commonData.deliveryLocation.trim(),
      dueDate: commonData.dueDate || "",
      lineNo: lineNo || row.lineNo || 1,
      isBoq,
      boq: buildBoqPayloadItems(row),
      materialCode: row.materialCode.trim(),
      materialDescription: row.materialDescription.trim(),
      hsnCode: row.hsnCode.trim(),
      uom: row.uom.trim(),
      quantity: parseQuantity(row.quantity),
      userId:
        currentUserRole === "user"
          ? originalOwnerId || loggedInUserId
          : row.userId || originalOwnerId || loggedInUserId,
      basicPrice: parseFloat(record.basicPrice) || 0,
      gst: parseFloat(record.gst) || 0,
      totalValue: parseFloat(record.totalValue) || 0,
      quotePrice: parseFloat(record.quotePrice) || 0,
      supplierReferences: getSupplierReferencesFromRecord(record),
      supplierIds: getSupplierIdsFromRecord(record),
      procurementRemarks: record.procurementRemarks || "",
      technicalQueries: record.technicalQueries || "",
      quotationStatus: record.quotationStatus || "Pending",
      quoteReference: record.quoteReference || "",
      updatedBy: loggedInUserId,
      attachments: getAttachmentsFromRecord(record),
    };
  };

  const parseSubmitResponse = (raw) => {
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw);
      } catch {
        return raw;
      }
    }
    return raw;
  };

  const buildCreatePayload = (row, loggedInUserId, lineNo) => {
    const isBoq = !!row.isBoq;
    return {
      action: "create-procurement",
      prNo: commonData.prNo.trim(),
      prSummary: commonData.prSummary.trim(),
      clientReference: selectedCustomer?.customerName || "",
      clientReferenceId: commonData.clientReferenceId,
      deliveryLocation: commonData.deliveryLocation.trim(),
      dueDate: commonData.dueDate || "",
      lineNo: lineNo || row.lineNo || 1,
      isBoq,
      boq: buildBoqPayloadItems(row),
      materialCode: row.materialCode.trim(),
      materialDescription: row.materialDescription.trim(),
      hsnCode: row.hsnCode.trim(),
      uom: row.uom.trim(),
      quantity: parseQuantity(row.quantity),
      userId:
        currentUserRole === "user"
          ? loggedInUserId
          : row.userId && row.userId.trim() !== ""
            ? row.userId
            : loggedInUserId,
      basicPrice: 0,
      gst: 0,
      totalValue: 0,
      quotePrice: 0,
      createdBy: loggedInUserId,
    };
  };

  const createDraftProcurementShell = async (loggedInUserId) => {
    const prNo = commonData.prNo.trim();
    const payload = {
      action: "create-draft-procurement",
      userId: loggedInUserId,
      createdBy: loggedInUserId,
      prSummary: commonData.prSummary.trim(),
      clientReference: commonData.clientReference.trim(),
      clientReferenceId: commonData.clientReferenceId || null,
      deliveryLocation: commonData.deliveryLocation.trim(),
      dueDate: commonData.dueDate || "",
      materialCode: "",
      materialDescription: "",
      hsnCode: "",
      uom: "",
      quantity: 0,
    };

    if (prNo) {
      payload.prNo = prNo;
    }

    const response = await axios.post(API_URL, payload);
    const responseData = parseSubmitResponse(response.data);
    const success =
      responseData?.success === true || responseData?.success === "true";

    if (!success) {
      throw new Error(
        responseData?.message || "Failed to create draft procurement record",
      );
    }

    const id = responseData?.data?._id || responseData?.data?.id;
    if (!id) {
      throw new Error("Draft record ID missing from response");
    }

    return id;
  };

  const resolveProcurementId = async (row, unusedDraftIds, loggedInUserId) => {
    if (row.existingId) {
      return row.existingId;
    }
    if (unusedDraftIds.length > 0) {
      return unusedDraftIds.shift();
    }
    if (isNewDraftFromAdd) {
      return createDraftProcurementShell(loggedInUserId);
    }
    return null;
  };

  // Add new row
  const handleAddRow = () => {
    setProcurementRows((prev) => {
      const newRow = {
        id: prev.length + 1,
        materialCode: "",
        materialDescription: "",
        hsnCode: "",
        uom: "",
        quantity: "",
        userId:
          currentUserRole !== "admin" && currentUserRole !== "supervisor"
            ? currentUserId
            : "",
        isExisting: false,
        isBoq: false,
        boq: [],
        lineNo: prev.length + 1,
      };
      return [...prev, newRow];
    });
  };

  const handleToggleBoq = (rowId) => {
    setProcurementRows((prev) =>
      prev.map((row) => {
        if (String(row.id) !== String(rowId)) return row;
        const nextIsBoq = !row.isBoq;
        return {
          ...row,
          isBoq: nextIsBoq,
          boq: nextIsBoq
            ? row.boq && row.boq.length > 0
              ? row.boq
              : [createEmptyBoqItem()]
            : row.boq || [],
        };
      }),
    );
  };

  const handleAddBoqItem = (parentId) => {
    setProcurementRows((prev) =>
      prev.map((row) =>
        String(row.id) === String(parentId)
          ? { ...row, isBoq: true, boq: [...(row.boq || []), createEmptyBoqItem()] }
          : row,
      ),
    );
  };

  const handleRemoveBoqItem = (parentId, boqId) => {
    setProcurementRows((prev) =>
      prev.map((row) => {
        if (String(row.id) !== String(parentId)) return row;
        const nextBoq = (row.boq || []).filter(
          (item) => String(item.id) !== String(boqId),
        );
        return {
          ...row,
          boq: nextBoq,
          isBoq: nextBoq.length > 0 ? row.isBoq : false,
        };
      }),
    );
  };

  const handleUpdateBoqItem = (parentId, boqId, field, value) => {
    setProcurementRows((prev) =>
      prev.map((row) => {
        if (String(row.id) !== String(parentId)) return row;
        return {
          ...row,
          boq: (row.boq || []).map((item) =>
            String(item.id) === String(boqId)
              ? { ...item, [field]: value }
              : item,
          ),
        };
      }),
    );
  };

  const handleUpdateBoqItemFields = (parentId, boqId, fields) => {
    setProcurementRows((prev) =>
      prev.map((row) => {
        if (String(row.id) !== String(parentId)) return row;
        return {
          ...row,
          boq: (row.boq || []).map((item) =>
            String(item.id) === String(boqId) ? { ...item, ...fields } : item,
          ),
        };
      }),
    );
  };

  // Remove row
  const handleRemoveRow = (id) => {
    const rowToRemove = procurementRows.find((row) => row.id === id);
    if (rowToRemove && rowToRemove.isExisting) {
      toast.warning(
        "Cannot remove existing records. You can only add new rows.",
        { autoClose: 2000 },
      );
      return;
    }

    const remainingRows = procurementRows.filter((row) => row.id !== id);
    const updatedRows = remainingRows.map((row, index) => ({
      ...row,
      id: index + 1,
    }));

    setProcurementRows(updatedRows);
  };

  // Update row field
  // Fixes stale-closure overwrite bug — use functional updates
  const handleUpdateRow = (id, field, value) => {
    setProcurementRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  };

  // New: update several fields on a row in one atomic call
  const handleUpdateRowFields = (id, fields) => {
    setProcurementRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...fields } : row)),
    );
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate common fields
    if (!commonData.prNo.trim()) {
      toast.error("PR Number is required", { autoClose: 2000 });
      return;
    }
    if (!commonData.clientReference.trim()) {
      toast.error("Client Reference is required", { autoClose: 2000 });
      return;
    }
    if (!commonData.deliveryLocation.trim()) {
      toast.error("Location is required", { autoClose: 2000 });
      return;
    }
    if (!String(commonData.dueDate || "").trim()) {
      toast.error("Due Date is required", { autoClose: 2000 });
      return;
    }

    // Get currentUserId from localStorage
    const loggedInUserId = localStorage.getItem("id");
    if (!loggedInUserId) {
      toast.error("User session expired. Please login again", {
        autoClose: 2000,
      });
      navigate("/");
      return;
    }

    const newRows = procurementRows.filter((row) => !row.isExisting);
    const existingRows = procurementRows.filter((row) => row.isExisting);

    if (newRows.length > 0) {
      const hasPartialNewRow = newRows.some(
        (row) => !isRowEmpty(row) && !isRowFullyFilled(row),
      );
      if (hasPartialNewRow) {
        toast.error(
          "Please complete all partially filled new rows before submitting.",
          { autoClose: 3000 },
        );
        return;
      }
    }

    const boqInvalidRow = procurementRows.find((row) => {
      if (!row.isBoq) return false;
      const items = row.boq || [];
      if (items.length === 0) return true;
      return items.some(
        (item) => isBoqItemEmpty(item) || !isBoqItemFullyFilled(item),
      );
    });
    if (boqInvalidRow) {
      const lineNo =
        procurementRows.findIndex(
          (r) => String(r.id) === String(boqInvalidRow.id),
        ) + 1;
      toast.error(
        `Line ${lineNo}: BOQ is enabled — fill all BOQ line item fields (Material Code, Description, HSN, UOM, Quantity). Empty BOQ rows are not allowed.`,
        { autoClose: 3500 },
      );
      return;
    }

    // Filter out completely empty rows
    const filledRows = procurementRows.filter(
      (row) => !row.isExisting && isRowFilledForSubmit(row),
    );

    if (
      filledRows.length === 0 &&
      !(
        (isFetchExistingMode || isDraftMode) &&
        existingRows.length > 0
      )
    ) {
      toast.error(
        "Please fill at least one field in any row before submitting",
        { autoClose: 2000 },
      );
      return;
    }

    if (filledRows.length > 0) {
      const emptyRowsCount =
        procurementRows.length - filledRows.length - existingRows.length;
      if (emptyRowsCount > 0) {
        toast.info(
          `${emptyRowsCount} empty ${emptyRowsCount === 1 ? "row was" : "rows were"} skipped`,
          { autoClose: 2000 },
        );
      }
    }

    setIsLoading(true);
    setImportResults(null);

    try {
      const unusedDraftIds = [...emptyDraftRecordIds];
      const existingRowsToSync =
        isFetchExistingMode || isDraftMode
          ? existingRows.filter((row) =>
              canRequestUserUpdateRecord(
                row.originalRecord || row,
                loggedInUserId,
                currentUserRole,
              ),
            )
          : [];
      const rowsToUpdate = [...existingRowsToSync, ...filledRows];

      if (rowsToUpdate.length === 0 && unusedDraftIds.length === 0) {
        toast.error(
          "No rows available to update. Change due date/common fields on an existing PR, or add a material line.",
          { autoClose: 3000 },
        );
        setIsLoading(false);
        return;
      }

      const updatePromises = [];
      const createPromises = [];

      for (const row of rowsToUpdate) {
        const lineNo =
          procurementRows.findIndex((r) => String(r.id) === String(row.id)) +
            1 ||
          row.lineNo ||
          1;

        const procurementId = await resolveProcurementId(
          row,
          unusedDraftIds,
          loggedInUserId,
        );

        if (procurementId) {
          updatePromises.push(
            axios.post(
              API_URL,
              buildUpdatePayload(
                { ...row, existingId: procurementId },
                loggedInUserId,
                lineNo,
              ),
            ),
          );
          continue;
        }

        createPromises.push(
          axios.post(
            API_URL,
            buildCreatePayload(row, loggedInUserId, lineNo),
          ),
        );
      }

      const deleteUnusedDraftPromises = unusedDraftIds.map((id) =>
        axios.post(API_URL, {
          action: "delete-procurement",
          id,
          requestUserId: loggedInUserId,
        }),
      );

      const responses = await Promise.all([
        ...updatePromises,
        ...createPromises,
        ...deleteUnusedDraftPromises,
      ]);

      let allSuccess = true;
      let errorMessage = "";

      responses.forEach((response, index) => {
        let responseData = response.data;
        if (typeof responseData === "string") {
          try {
            responseData = JSON.parse(responseData);
          } catch (parseError) {
            responseData = response.data;
          }
        }

        if (responseData.success !== true) {
          setImportResults(null);
          allSuccess = false;
          errorMessage =
            responseData.message ||
            `Failed to save procurement item ${index + 1}`;
        }
      });

      if (allSuccess) {
        const updatedCount = updatePromises.length;
        const createdCount = createPromises.length;
        let successMessage = "";

        if (updatedCount > 0 && createdCount > 0) {
          successMessage = `${updatedCount} ${
            updatedCount > 1 ? "entries updated" : "entry updated"
          } and ${createdCount} new ${
            createdCount > 1 ? "entries created" : "entry created"
          } successfully`;
        } else if (updatedCount > 0) {
          successMessage = `${updatedCount} procurement ${
            updatedCount > 1 ? "entries" : "entry"
          } updated successfully`;
        } else {
          successMessage = `${createdCount} procurement ${
            createdCount > 1 ? "entries" : "entry"
          } created successfully`;
        }

        toast.success(successMessage, { autoClose: 3000 });
        setEmptyDraftRecordIds([]);
        setTimeout(() => {
          navigate("/ProcurementTracker");
        }, 1500);
      } else {
        toast.error(errorMessage, { autoClose: 2000 });
      }
    } catch (error) {
      console.error("Save procurement error:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "An error occurred while saving procurement entries",
        { autoClose: 2000 },
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Add Purchase Request</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/ProcurementTracker">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card card-primary card-outline">
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fa-solid fa-table mr-2"></i>
                    Purchase Entry Form
                  </h3>
                  <div className="card-tools">
                    <span className="badge badge-info">
                      <i className="fa-solid fa-list mr-1"></i>
                      {procurementRows.length}{" "}
                      {procurementRows.length === 1 ? "Row" : "Rows"}
                    </span>
                  </div>
                </div>

                {/* Manual Entry Form */}
                <form onSubmit={handleSubmit}>
                  <div className="card-body">
                    {/* Common Fields Section */}
                    <div className="row mb-4 align-items-start">
                      <div className="col-6 col-md-2">
                        <div className="form-group mb-2 mb-md-0">
                          <label
                            style={{ fontWeight: "600", fontSize: "13px" }}
                          >
                            PR NUMBER
                          </label>
                          <input
                            type="text"
                            name="prNo"
                            className="form-control"
                            placeholder="PR Number"
                            value={commonData.prNo}
                            disabled
                            required
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-3">
                        <div className="form-group mb-2 mb-md-0">
                          <label
                            style={{ fontWeight: "600", fontSize: "13px" }}
                          >
                            Client Reference
                          </label>
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder={
                                isCustomersLoading
                                  ? "Loading clients..."
                                  : "Type to search client..."
                              }
                              value={customerSearchTerm}
                              onChange={handleCustomerSearchChange}
                              onKeyDown={handleCustomerKeyDown}
                              onFocus={() => setShowCustomerDropdown(true)}
                              onBlur={() => {
                                setTimeout(() => {
                                  setShowCustomerDropdown(false);
                                  setSelectedCustomerIndex(-1);
                                }, 200);
                              }}
                              autoComplete="off"
                              disabled={isCustomersLoading}
                              required
                            />

                            {isCustomersLoading && (
                              <div
                                style={{
                                  position: "absolute",
                                  right: "10px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  pointerEvents: "none",
                                }}
                              >
                                <i className="fa fa-spinner fa-spin text-muted"></i>
                              </div>
                            )}

                            {showCustomerDropdown &&
                              !isCustomersLoading &&
                              filteredCustomers.length > 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                    backgroundColor: "#fff",
                                    border: "1px solid #ced4da",
                                    borderRadius: "4px",
                                    zIndex: 1000,
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  {filteredCustomers.map((c, index) => (
                                    <div
                                      key={c._id}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleCustomerSelect(c);
                                      }}
                                      style={{
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                        borderBottom: "1px solid #f0f0f0",
                                        backgroundColor:
                                          index === selectedCustomerIndex
                                            ? "#e9ecef"
                                            : "#fff",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                          "#f8f9fa";
                                        setSelectedCustomerIndex(index);
                                      }}
                                      onMouseLeave={(e) => {
                                        if (index !== selectedCustomerIndex) {
                                          e.currentTarget.style.backgroundColor =
                                            "#fff";
                                        }
                                      }}
                                    >
                                      {c.customerName}
                                    </div>
                                  ))}
                                </div>
                              )}

                            {showCustomerDropdown &&
                              !isCustomersLoading &&
                              customerSearchTerm &&
                              filteredCustomers.length === 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    backgroundColor: "#fff",
                                    border: "1px solid #ced4da",
                                    borderRadius: "4px",
                                    padding: "8px 12px",
                                    zIndex: 1000,
                                    color: "#6c757d",
                                    fontSize: "13px",
                                  }}
                                >
                                  No clients found
                                </div>
                              )}
                          </div>

                          {commonData.clientReferenceId && (
                            <small className="form-text text-muted">
                              <i className="fa fa-check-circle text-success mr-1"></i>
                              Selected: {commonData.clientReference}
                            </small>
                          )}
                        </div>
                      </div>
                      <div className="col-6 col-md-2">
                        <div className="form-group mb-2 mb-md-0">
                          <label
                            style={{ fontWeight: "600", fontSize: "13px" }}
                          >
                            Location
                          </label>
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Type to search location..."
                              value={locationSearchTerm}
                              onChange={handleLocationSearchChange}
                              onKeyDown={handleLocationKeyDown}
                              onFocus={() => setShowLocationDropdown(true)}
                              onBlur={() => {
                                setTimeout(() => {
                                  setShowLocationDropdown(false);
                                  setSelectedLocationIndex(-1);
                                }, 200);
                              }}
                              autoComplete="off"
                              required
                            />

                            {showLocationDropdown &&
                              filteredLocations.length > 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                    backgroundColor: "#fff",
                                    border: "1px solid #ced4da",
                                    borderRadius: "4px",
                                    zIndex: 1000,
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  {filteredLocations.map((loc, index) => (
                                    <div
                                      key={index}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleLocationSelect(loc);
                                      }}
                                      style={{
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                        borderBottom: "1px solid #f0f0f0",
                                        backgroundColor:
                                          index === selectedLocationIndex
                                            ? "#e9ecef"
                                            : "#fff",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                          "#f8f9fa";
                                        setSelectedLocationIndex(index);
                                      }}
                                      onMouseLeave={(e) => {
                                        if (index !== selectedLocationIndex) {
                                          e.currentTarget.style.backgroundColor =
                                            "#fff";
                                        }
                                      }}
                                    >
                                      {loc}
                                    </div>
                                  ))}
                                </div>
                              )}

                            {showLocationDropdown &&
                              locationSearchTerm &&
                              filteredLocations.length === 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    backgroundColor: "#fff",
                                    border: "1px solid #ced4da",
                                    borderRadius: "4px",
                                    padding: "8px 12px",
                                    zIndex: 1000,
                                    color: "#6c757d",
                                    fontSize: "13px",
                                  }}
                                >
                                  No locations found
                                </div>
                              )}
                          </div>

                          {commonData.deliveryLocation && (
                            <small className="form-text text-muted">
                              <i className="fa fa-check-circle text-success mr-1"></i>
                              Selected: {commonData.deliveryLocation}
                            </small>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-md-3">
                        <div className="form-group mb-2 mb-md-0">
                          <label
                            style={{ fontWeight: "600", fontSize: "13px" }}
                          >
                            PR Summary
                          </label>
                          <textarea
                            name="prSummary"
                            className="form-control"
                            rows={1}
                            placeholder="Summary"
                            value={commonData.prSummary}
                            onChange={handleCommonChange}
                            style={{
                              resize: "vertical",
                              minHeight: "38px",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-6 col-md-2">
                        <div className="form-group mb-0">
                          <label
                            style={{ fontWeight: "600", fontSize: "13px" }}
                          >
                            Due Date <span style={{ color: "#dc3545" }}>*</span>
                          </label>
                          <input
                            type="date"
                            name="dueDate"
                            className="form-control"
                            value={commonData.dueDate || ""}
                            onChange={handleCommonChange}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-end mb-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-success"
                        onClick={handleAddRow}
                        disabled={isLoading || isFetchingData}
                        title="Add New Row"
                        style={{
                          padding: "6px 14px",
                          fontWeight: "600",
                          borderRadius: "20px",
                        }}
                      >
                        <i className="fa-solid fa-plus mr-1" />
                        Add
                      </button>
                    </div>

                    {/* Material Details Table */}
                    <div style={{ overflowX: "auto", maxWidth: "100%" }}>
                      <table
                        className="table table-bordered table-hover mb-0"
                        style={{ width: "100%", tableLayout: "auto" }}
                      >
                        <thead>
                          <tr
                            style={{
                              backgroundColor: "#003366",
                              color: "white",
                            }}
                          >
                            <th
                              style={{
                                padding: "10px 6px",
                                fontSize: "12px",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                                minWidth: "60px",
                              }}
                            >
                              S.NO
                            </th>
                            <th
                              style={{
                                padding: "12px 8px",
                                fontSize: "12px",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                                minWidth: "100px",
                                width: "140px",
                              }}
                            >
                              Material Code
                            </th>
                            <th
                              style={{
                                padding: "12px 8px",
                                fontSize: "12px",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                                minWidth: "220px",
                                width: "280px",
                              }}
                            >
                              Material Description
                            </th>
                            <th
                              style={{
                                padding: "12px 8px",
                                fontSize: "12px",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                                minWidth: "50px",
                              }}
                            >
                              HSN
                            </th>
                            <th
                              style={{
                                padding: "12px 8px",
                                fontSize: "12px",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                                minWidth: "50px",
                              }}
                            >
                              UOM
                            </th>
                            <th
                              style={{
                                padding: "12px 8px",
                                fontSize: "12px",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                                minWidth: "50px",
                              }}
                            >
                              Quantity
                            </th>
                            {(currentUserRole === "admin" ||
                              currentUserRole === "supervisor") && (
                              <th
                                style={{
                                  padding: "12px 8px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  whiteSpace: "nowrap",
                                  minWidth: "130px",
                                }}
                              >
                                Assigned User
                              </th>
                            )}
                            <th
                              style={{
                                padding: "12px 8px",
                                fontSize: "12px",
                                fontWeight: "600",
                                textAlign: "center",
                                whiteSpace: "nowrap",
                                minWidth: "60px",
                                width: "64px",
                              }}
                            >
                              BOQ
                            </th>
                            <th
                              style={{
                                padding: "12px 8px",
                                fontSize: "12px",
                                fontWeight: "600",
                                textAlign: "center",
                                whiteSpace: "nowrap",
                                minWidth: "100px",
                              }}
                            >
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {isFetchingData ? (
                            <tr>
                              <td
                                colSpan={
                                  currentUserRole === "admin" ||
                                  currentUserRole === "supervisor"
                                    ? 9
                                    : 8
                                }
                                className="text-center py-4"
                              >
                                <div className="d-flex justify-content-center align-items-center">
                                  <div
                                    className="spinner-border spinner-border-sm text-primary mr-2"
                                    role="status"
                                  >
                                    <span className="sr-only">Loading...</span>
                                  </div>
                                  <span>
                                    {isDraftMode ? "Loading..." : "Loading..."}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ) : procurementRows.length === 0 ? (
                            <tr>
                              <td
                                colSpan={
                                  currentUserRole === "admin" ||
                                  currentUserRole === "supervisor"
                                    ? 9
                                    : 8
                                }
                                className="text-center py-4 text-muted"
                              >
                                Click <strong>Add</strong> to create a new entry
                                row
                              </td>
                            </tr>
                          ) : (
                            procurementRows.flatMap((row, index) => {
                              const lineNo = index + 1;
                              const parentRow = (
                              <tr
                                key={row.id}
                                style={{
                                  backgroundColor: row.isExisting
                                    ? "#f0f8ff"
                                    : index % 2 === 0
                                      ? "#ffffff"
                                      : "#f8f9fa",
                                }}
                              >
                                <td
                                  style={{
                                    padding: "6px",
                                    fontWeight: 600,
                                  }}
                                >
                                  {lineNo}
                                </td>
                                <td style={{ padding: "6px" }}>
                                  <div
                                    style={{
                                      position: "relative",
                                      width: "100%",
                                      minWidth: "120px",
                                      maxWidth: "160px",
                                    }}
                                  >
                                    <input
                                      ref={(el) =>
                                        (materialInputRefs.current[row.id] = el)
                                      }
                                      type="text"
                                      className="form-control form-control-sm"
                                      placeholder={
                                        isMaterialsLoading
                                          ? "Loading materials..."
                                          : "Search Material..."
                                      }
                                      value={materialSearchTerms[row.id] || ""}
                                      onChange={(e) =>
                                        handleMaterialSearchChange(
                                          row.id,
                                          e.target.value,
                                        )
                                      }
                                      onKeyDown={(e) =>
                                        handleMaterialKeyDown(row.id, e)
                                      }
                                      onFocus={() =>
                                        openMaterialDropdown(row.id)
                                      }
                                      onBlur={() => {
                                        setTimeout(() => {
                                          setShowMaterialDropdown((prev) => ({
                                            ...prev,
                                            [row.id]: false,
                                          }));
                                          setSelectedMaterialIndex((prev) => ({
                                            ...prev,
                                            [row.id]: -1,
                                          }));
                                        }, 200);
                                      }}
                                      autoComplete="off"
                                      disabled={
                                        isLoading ||
                                        row.isExisting ||
                                        isMaterialsLoading
                                      }
                                      readOnly={row.isExisting}
                                      style={{
                                        width: "100%",
                                        paddingRight: isMaterialsLoading
                                          ? "28px"
                                          : undefined,
                                        borderColor:
                                          duplicateMaterialRowId === row.id
                                            ? "#dc3545"
                                            : undefined,
                                      }}
                                    />

                                    {isMaterialsLoading && (
                                      <div
                                        style={{
                                          position: "absolute",
                                          right: "10px",
                                          top: "50%",
                                          transform: "translateY(-50%)",
                                          pointerEvents: "none",
                                        }}
                                      >
                                        <i className="fa fa-spinner fa-spin text-muted"></i>
                                      </div>
                                    )}

                                    {duplicateMaterialRowId === row.id && (
                                      <small
                                        className="text-danger d-block mt-1"
                                        style={{ fontSize: "11px" }}
                                      >
                                        Duplicate material code
                                      </small>
                                    )}

                                    {/* dropdown portal trigger stays the same */}
                                  </div>
                                </td>
                                <td style={{ padding: "6px" }}>
  <div
    style={{
      position: "relative",
      width: "100%",
      minWidth: "180px",
      maxWidth: "280px",
    }}
  >
    <textarea
      ref={(el) => (materialDescInputRefs.current[row.id] = el)}
      className="form-control form-control-sm"
      rows={2}
      placeholder={
        isMaterialsLoading
          ? "Loading materials..."
          : "Search Material Description..."
      }
      value={materialDescriptionSearchTerms[row.id] || ""}
      onChange={(e) =>
        handleMaterialDescriptionSearchChange(
          row.id,
          e.target.value
        )
      }
      onKeyDown={(e) =>
        handleMaterialDescriptionKeyDown(row.id, e)
      }
      onFocus={() => openMaterialDescriptionDropdown(row.id)}
      onBlur={() => {
        setTimeout(() => {
          setShowMaterialDescriptionDropdown((prev) => ({
            ...prev,
            [row.id]: false,
          }));
          setSelectedMaterialDescriptionIndex((prev) => ({
            ...prev,
            [row.id]: -1,
          }));
        }, 200);
      }}
      autoComplete="off"
      disabled={
        isLoading ||
        row.isExisting ||
        isMaterialsLoading
      }
      readOnly={row.isExisting}
      style={{
        fontSize: "12px",
        width: "100%",
        resize: "vertical",
        backgroundColor: row.isExisting ? "#e9ecef" : "#fff",
      }}
    />
  </div>
</td>
                                <td style={{ padding: "6px" }}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={row.hsnCode}
                                    onChange={(e) =>
                                      handleUpdateRow(
                                        row.id,
                                        "hsnCode",
                                        e.target.value,
                                      )
                                    }
                                    disabled={isLoading || row.isExisting}
                                    placeholder="HSN"
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "70px",
                                      backgroundColor: row.isExisting
                                        ? "#e9ecef"
                                        : "white",
                                    }}
                                    readOnly={row.isExisting}
                                  />
                                </td>
                                <td style={{ padding: "6px" }}>
                                  <select
                                    className="form-control form-control-sm"
                                    value={row.uom || ""}
                                    onChange={(e) =>
                                      handleUpdateRow(
                                        row.id,
                                        "uom",
                                        e.target.value,
                                      )
                                    }
                                    disabled={
                                      isLoading ||
                                      row.isExisting ||
                                      isLoadingUnits
                                    }
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "80px",
                                      backgroundColor: row.isExisting
                                        ? "#e9ecef"
                                        : "white",
                                    }}
                                  >
                                    <option value="">
                                      {isLoadingUnits ? "Loading..." : "UOM"}
                                    </option>
                                    {row.uom &&
                                      !unitOptions.some(
                                        (opt) =>
                                          opt.unit.toLowerCase() ===
                                          String(row.uom).toLowerCase(),
                                      ) && (
                                        <option value={row.uom}>
                                          {row.uom}
                                        </option>
                                      )}
                                    {unitOptions.map((opt) => (
                                      <option
                                        key={opt.id || opt.unit}
                                        value={opt.unit}
                                      >
                                        {opt.unit}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td style={{ padding: "6px" }}>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    value={row.quantity}
                                    onChange={(e) =>
                                      handleUpdateRow(
                                        row.id,
                                        "quantity",
                                        e.target.value,
                                      )
                                    }
                                    disabled={isLoading || row.isExisting}
                                    placeholder="Qty"
                                    min="0"
                                    step="any"
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "70px",
                                      backgroundColor: row.isExisting
                                        ? "#e9ecef"
                                        : "white",
                                    }}
                                    readOnly={row.isExisting}
                                  />
                                </td>
                                {(currentUserRole === "admin" ||
                                  currentUserRole === "supervisor") && (
                                  <td style={{ padding: "6px" }}>
                                    <select
                                      className="form-control form-control-sm"
                                      value={row.userId}
                                      onChange={(e) =>
                                        handleUpdateRow(
                                          row.id,
                                          "userId",
                                          e.target.value,
                                        )
                                      }
                                      disabled={isLoading || row.isExisting}
                                      style={{
                                        fontSize: "12px",
                                        minWidth: "110px",
                                        backgroundColor: row.isExisting
                                          ? "#e9ecef"
                                          : "white",
                                      }}
                                    >
                                      <option value="">Select</option>
                                      {users.map((user) => (
                                        <option key={user.id} value={user.id}>
                                          {user.name}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                )}
                                <td
                                  style={{
                                    padding: "6px",
                                    textAlign: "center",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={row.isBoq}
                                    aria-label="BOQ toggle"
                                    onClick={() => handleToggleBoq(row.id)}
                                    disabled={isLoading}
                                    title={
                                      row.isBoq ? "BOQ enabled" : "BOQ disabled"
                                    }
                                    style={{
                                      position: "relative",
                                      width: "52px",
                                      height: "26px",
                                      borderRadius: "13px",
                                      border: "none",
                                      padding: 0,
                                      cursor: isLoading
                                        ? "not-allowed"
                                        : "pointer",
                                      backgroundColor: row.isBoq
                                        ? "#0094a9"
                                        : "#ced4da",
                                      transition: "background-color 0.2s ease",
                                      outline: "none",
                                      boxShadow: "none",
                                      verticalAlign: "middle",
                                    }}
                                  >
                                    <span
                                      style={{
                                        position: "absolute",
                                        top: "3px",
                                        left: row.isBoq ? "28px" : "3px",
                                        width: "20px",
                                        height: "20px",
                                        borderRadius: "50%",
                                        backgroundColor: "#fff",
                                        boxShadow:
                                          "0 1px 3px rgba(0,0,0,0.25)",
                                        transition: "left 0.2s ease",
                                      }}
                                    />
                                    <span
                                      style={{
                                        position: "absolute",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        left: row.isBoq ? "6px" : "auto",
                                        right: row.isBoq ? "auto" : "6px",
                                        fontSize: "9px",
                                        fontWeight: 700,
                                        letterSpacing: "0.02em",
                                        color: row.isBoq ? "#fff" : "#495057",
                                        lineHeight: 1,
                                        pointerEvents: "none",
                                      }}
                                    >
                                      {row.isBoq ? "ON" : "OFF"}
                                    </span>
                                  </button>
                                </td>
                                <td
                                  style={{
                                    padding: "6px",
                                    textAlign: "center",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.isBoq && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-success mr-1"
                                      onClick={() => handleAddBoqItem(row.id)}
                                      disabled={isLoading}
                                      title="Add BOQ line"
                                      style={{
                                        padding: "3px 7px",
                                        borderRadius: "50%",
                                      }}
                                    >
                                      <i className="fa-solid fa-plus" />
                                    </button>
                                  )}
                                  {row.isExisting ? (
                                    <span
                                      className="badge badge-info"
                                      style={{ fontSize: "10px" }}
                                    >
                                      <i className="fa-solid fa-check mr-1"></i>
                                      Existing
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger"
                                      onClick={() => handleRemoveRow(row.id)}
                                      disabled={isLoading}
                                      title="Remove This Row"
                                      style={{ padding: "3px 8px" }}
                                    >
                                      <i className="fa-solid fa-trash" />
                                    </button>
                                  )}
                                </td>
                              </tr>
                              );

                              const boqRows =
                                row.isBoq && Array.isArray(row.boq)
                                  ? row.boq.map((boqItem, boqIndex) => {
                                      const boqKey = makeBoqSearchKey(
                                        row.id,
                                        boqItem.id,
                                      );
                                      return (
                                        <tr
                                          key={boqKey}
                                          style={{
                                            backgroundColor: "#fff8e6",
                                          }}
                                        >
                                          <td
                                            style={{
                                              padding: "6px",
                                              paddingLeft: "14px",
                                              fontWeight: 600,
                                              color: "#856404",
                                            }}
                                          >
                                            {lineNo}.{boqIndex + 1}
                                          </td>
                                          <td style={{ padding: "6px" }}>
                                            <div
                                              style={{
                                                position: "relative",
                                                width: "100%",
                                                minWidth: "120px",
                                                maxWidth: "160px",
                                              }}
                                            >
                                              <input
                                                ref={(el) =>
                                                  (materialInputRefs.current[
                                                    boqKey
                                                  ] = el)
                                                }
                                                type="text"
                                                className="form-control form-control-sm"
                                                placeholder="Search Material..."
                                                value={
                                                  materialSearchTerms[boqKey] ||
                                                  ""
                                                }
                                                onChange={(e) =>
                                                  handleMaterialSearchChange(
                                                    boqKey,
                                                    e.target.value,
                                                  )
                                                }
                                                onKeyDown={(e) =>
                                                  handleMaterialKeyDown(
                                                    boqKey,
                                                    e,
                                                  )
                                                }
                                                onFocus={() =>
                                                  openMaterialDropdown(boqKey)
                                                }
                                                onBlur={() => {
                                                  setTimeout(() => {
                                                    setShowMaterialDropdown(
                                                      (prev) => ({
                                                        ...prev,
                                                        [boqKey]: false,
                                                      }),
                                                    );
                                                  }, 200);
                                                }}
                                                autoComplete="off"
                                                disabled={
                                                  isLoading || isMaterialsLoading
                                                }
                                                style={{ width: "100%" }}
                                              />
                                            </div>
                                          </td>
                                          <td style={{ padding: "6px" }}>
                                            <div
                                              style={{
                                                position: "relative",
                                                width: "100%",
                                                minWidth: "180px",
                                                maxWidth: "280px",
                                              }}
                                            >
                                              <textarea
                                                ref={(el) =>
                                                  (materialDescInputRefs.current[
                                                    boqKey
                                                  ] = el)
                                                }
                                                className="form-control form-control-sm"
                                                rows={2}
                                                placeholder="Material Description..."
                                                value={
                                                  materialDescriptionSearchTerms[
                                                    boqKey
                                                  ] || ""
                                                }
                                                onChange={(e) =>
                                                  handleMaterialDescriptionSearchChange(
                                                    boqKey,
                                                    e.target.value,
                                                  )
                                                }
                                                onKeyDown={(e) =>
                                                  handleMaterialDescriptionKeyDown(
                                                    boqKey,
                                                    e,
                                                  )
                                                }
                                                onFocus={() =>
                                                  openMaterialDescriptionDropdown(
                                                    boqKey,
                                                  )
                                                }
                                                onBlur={() => {
                                                  setTimeout(() => {
                                                    setShowMaterialDescriptionDropdown(
                                                      (prev) => ({
                                                        ...prev,
                                                        [boqKey]: false,
                                                      }),
                                                    );
                                                  }, 200);
                                                }}
                                                autoComplete="off"
                                                disabled={
                                                  isLoading || isMaterialsLoading
                                                }
                                                style={{
                                                  fontSize: "12px",
                                                  width: "100%",
                                                  resize: "vertical",
                                                }}
                                              />
                                            </div>
                                          </td>
                                          <td style={{ padding: "6px" }}>
                                            <input
                                              type="text"
                                              className="form-control form-control-sm"
                                              value={boqItem.hsnCode}
                                              onChange={(e) =>
                                                handleUpdateBoqItem(
                                                  row.id,
                                                  boqItem.id,
                                                  "hsnCode",
                                                  e.target.value,
                                                )
                                              }
                                              disabled={isLoading}
                                              placeholder="HSN"
                                              style={{
                                                fontSize: "12px",
                                                minWidth: "70px",
                                              }}
                                            />
                                          </td>
                                          <td style={{ padding: "6px" }}>
                                            <select
                                              className="form-control form-control-sm"
                                              value={boqItem.uom || ""}
                                              onChange={(e) =>
                                                handleUpdateBoqItem(
                                                  row.id,
                                                  boqItem.id,
                                                  "uom",
                                                  e.target.value,
                                                )
                                              }
                                              disabled={
                                                isLoading || isLoadingUnits
                                              }
                                              style={{
                                                fontSize: "12px",
                                                minWidth: "80px",
                                              }}
                                            >
                                              <option value="">
                                                {isLoadingUnits
                                                  ? "Loading..."
                                                  : "UOM"}
                                              </option>
                                              {boqItem.uom &&
                                                !unitOptions.some(
                                                  (opt) =>
                                                    opt.unit.toLowerCase() ===
                                                    String(
                                                      boqItem.uom,
                                                    ).toLowerCase(),
                                                ) && (
                                                  <option value={boqItem.uom}>
                                                    {boqItem.uom}
                                                  </option>
                                                )}
                                              {unitOptions.map((opt) => (
                                                <option
                                                  key={opt.id || opt.unit}
                                                  value={opt.unit}
                                                >
                                                  {opt.unit}
                                                </option>
                                              ))}
                                            </select>
                                          </td>
                                          <td style={{ padding: "6px" }}>
                                            <input
                                              type="number"
                                              className="form-control form-control-sm"
                                              value={boqItem.quantity}
                                              onChange={(e) =>
                                                handleUpdateBoqItem(
                                                  row.id,
                                                  boqItem.id,
                                                  "quantity",
                                                  e.target.value,
                                                )
                                              }
                                              disabled={isLoading}
                                              placeholder="Qty"
                                              min="0"
                                              step="any"
                                              style={{
                                                fontSize: "12px",
                                                minWidth: "70px",
                                              }}
                                            />
                                          </td>
                                          {(currentUserRole === "admin" ||
                                            currentUserRole ===
                                              "supervisor") && (
                                            <td
                                              style={{
                                                padding: "6px",
                                                textAlign: "center",
                                                color: "#adb5bd",
                                                fontSize: "11px",
                                              }}
                                            >
                                              —
                                            </td>
                                          )}
                                          <td
                                            style={{
                                              padding: "6px",
                                              textAlign: "center",
                                              color: "#adb5bd",
                                              fontSize: "11px",
                                            }}
                                          >
                                            —
                                          </td>
                                          <td
                                            style={{
                                              padding: "6px",
                                              textAlign: "center",
                                            }}
                                          >
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-danger"
                                              onClick={() =>
                                                handleRemoveBoqItem(
                                                  row.id,
                                                  boqItem.id,
                                                )
                                              }
                                              disabled={isLoading}
                                              title="Remove BOQ line"
                                              style={{ padding: "3px 8px" }}
                                            >
                                              <i className="fa-solid fa-trash" />
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })
                                  : [];

                              return [parentRow, ...boqRows];
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                    {Object.keys(showMaterialDropdown).map((rowId) => {
                      if (!showMaterialDropdown[rowId]) return null;
                      const pos = materialDropdownPos[rowId];
                      if (!pos) return null;

                      const filtered = getFilteredMaterials(rowId);
                      const boqRef = parseBoqSearchKey(rowId);
                      const rowExists = boqRef
                        ? procurementRows.some(
                            (r) => String(r.id) === String(boqRef.parentId),
                          )
                        : procurementRows.some(
                            (r) => String(r.id) === String(rowId),
                          );
                      if (!rowExists) return null;

                      return createPortal(
                        <div
                          style={{
                            position: "fixed",
                            top: pos.top,
                            left: pos.left,
                            width: pos.width,
                            maxHeight: "240px",
                            overflowY: "auto",
                            backgroundColor: "#fff",
                            border: "1px solid #ced4da",
                            borderRadius: "4px",
                            zIndex: 20000,
                            boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                          }}
                        >
                          {filtered.length > 0
                            ? filtered.map((material, index) => (
                                <div
                                  key={material._id}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleMaterialSelect(rowId, material);
                                  }}
                                  onMouseEnter={() =>
                                    setSelectedMaterialIndex((prev) => ({
                                      ...prev,
                                      [rowId]: index,
                                    }))
                                  }
                                  style={{
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    borderBottom: "1px solid #f0f0f0",
                                    backgroundColor:
                                      index ===
                                      (selectedMaterialIndex[rowId] ?? -1)
                                        ? "#e9ecef"
                                        : "#fff",
                                  }}
                                >
                                  <strong>{material.materialCode}</strong>
                                </div>
                              ))
                            : materialSearchTerms[rowId] && (
                                <div
                                  style={{
                                    padding: "8px 12px",
                                    color: "#6c757d",
                                    fontSize: "13px",
                                  }}
                                >
                                  No materials found
                                </div>
                              )}
                        </div>,
                        document.body,
                      );
                    })}
                    {Object.keys(showMaterialDescriptionDropdown).map((rowId) => {
  if (!showMaterialDescriptionDropdown[rowId]) return null;

  const pos = materialDescriptionDropdownPos[rowId];
  if (!pos) return null;

  const filtered = getFilteredMaterialDescriptions(rowId);
  const boqRef = parseBoqSearchKey(rowId);
  const rowExists = boqRef
    ? procurementRows.some(
        (r) => String(r.id) === String(boqRef.parentId),
      )
    : procurementRows.some((r) => String(r.id) === String(rowId));

  if (!rowExists) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        width: pos.width,
        maxHeight: "240px",
        overflowY: "auto",
        backgroundColor: "#fff",
        border: "1px solid #ced4da",
        borderRadius: "4px",
        zIndex: 20000,
        boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
      }}
    >
      {filtered.map((material, index) => (
        <div
          key={material._id}
          onMouseDown={(e) => {
            e.preventDefault();
            handleMaterialDescriptionSelect(rowId, material);
          }}
          onMouseEnter={() =>
            setSelectedMaterialDescriptionIndex((prev) => ({
              ...prev,
              [rowId]: index,
            }))
          }
          style={{
            padding: "8px 12px",
            cursor: "pointer",
            borderBottom: "1px solid #f0f0f0",
            backgroundColor:
              index ===
              (selectedMaterialDescriptionIndex[rowId] ?? -1)
                ? "#e9ecef"
                : "#fff",
          }}
        >
          <strong>{material.productName}</strong>
        </div>
      ))}
    </div>,
    document.body
  );
})}
                  </div>
                  <div
                    className="card-footer text-center"
                    style={{ backgroundColor: "#ffffff", padding: "20px" }}
                  >
                    {isDirty && (
                      <button
                        type="submit"
                        className="btn btn-lg"
                        disabled={isLoading}
                        style={{
                          backgroundColor: "#17a2b8",
                          color: "white",
                          padding: "10px 30px",
                          fontSize: "13px",
                          fontWeight: "600",
                          borderRadius: "25px",
                          border: "none",
                          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {isLoading ? (
                          <>
                            <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-check mr-2"></i>
                            Submit
                          </>
                        )}
                      </button>
                    )}

                    <div className="d-flex justify-content-center mt-4">
                      <div
                        className="card shadow-sm"
                        style={{
                          width: "600px",
                          borderRadius: "8px",
                          backgroundColor: "#e9ecef",
                        }}
                      >
                        <div
                          style={{
                            borderTop: "4px solid #17a2b8",
                            padding: "12px 20px",
                            fontWeight: "600",
                            fontSize: "16px",
                          }}
                        >
                          Upload Excel File
                        </div>

                        <div style={{ padding: "20px" }}>
                          <div className="d-flex">
                            <input
                              type="text"
                              className="form-control"
                              value={
                                importFile ? importFile.name : "Choose file"
                              }
                              readOnly
                              style={{
                                borderTopRightRadius: "0",
                                borderBottomRightRadius: "0",
                                fontSize: "13px",
                              }}
                            />

                            <button
                              type="button"
                              className="btn"
                              style={{
                                backgroundColor: "#17a2b8",
                                color: "white",
                                borderTopLeftRadius: "0",
                                borderBottomLeftRadius: "0",
                                padding: "6px 20px",
                              }}
                              onClick={() =>
                                document
                                  .getElementById("procurementExcel")
                                  .click()
                              }
                            >
                              Browse
                            </button>
                          </div>

                          <input
                            type="file"
                            id="procurementExcel"
                            accept=".xlsx,.xls"
                            style={{ display: "none" }}
                            onChange={handleImportFileChange}
                          />

                          <p
                            className="text-muted mt-2 mb-0"
                            style={{ fontSize: "13px" }}
                          >
                            Only Excel files (.xlsx, .xls) are supported.
                          </p>

                          <div className="mt-2">
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              onClick={handleDownloadTemplate}
                              disabled={isDownloadingSample}
                              style={{
                                fontSize: "13px",
                                textDecoration: "none",
                              }}
                            >
                              {isDownloadingSample
                                ? "Preparing download..."
                                : "Download sample Excel format"}
                            </button>
                          </div>
                        </div>

                        <div
                          className="text-center"
                          style={{
                            padding: "15px",
                            backgroundColor: "#f8f9fa",
                          }}
                        >
                          <button
                            type="button"
                            onClick={handleExcelImport}
                            disabled={!importFile || isImporting}
                            style={{
                              backgroundColor: "#5bb7c5",
                              color: "white",
                              border: "none",
                              padding: "8px 30px",
                              borderRadius: "20px",
                              fontSize: "13px",
                            }}
                          >
                            {isImporting ? "Importing..." : "Import"}
                          </button>
                        </div>
                        {importResults && (
                          <div className="card card-primary card-outline mt-3">
                            <div className="card-header">
                              <h3 className="card-title">Import Results</h3>
                            </div>
                            <div className="card-body import-results">
                              <div className="row mb-3">
                                <div className="col-lg-3 col-md-6 col-sm-6 mb-3">
                                  <div className="info-box">
                                    <span className="info-box-icon bg-info elevation-1">
                                      <i className="fa-solid fa-list"></i>
                                    </span>
                                    <div className="info-box-content">
                                      <span className="info-box-text">
                                        Total Records
                                      </span>
                                      <span className="info-box-number">
                                        {importResults.total || 0}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-lg-3 col-md-6 col-sm-6 mb-3">
                                  <div className="info-box">
                                    <span className="info-box-icon bg-success elevation-1">
                                      <i className="fa-solid fa-check"></i>
                                    </span>
                                    <div className="info-box-content">
                                      <span className="info-box-text">
                                        Imported Records
                                      </span>
                                      <span className="info-box-number">
                                        {importResults.imported || 0}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-lg-3 col-md-6 col-sm-6 mb-3">
                                  <div className="info-box">
                                    <span className="info-box-icon bg-warning elevation-1">
                                      <i className="fa-solid fa-exclamation"></i>
                                    </span>
                                    <div className="info-box-content">
                                      <span className="info-box-text">
                                        Skipped Records
                                      </span>
                                      <span className="info-box-number">
                                        {importResults.skipped || 0}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-lg-3 col-md-6 col-sm-6 mb-3">
                                  <div className="info-box">
                                    <span className="info-box-icon bg-danger elevation-1">
                                      <i className="fa-solid fa-times"></i>
                                    </span>
                                    <div className="info-box-content">
                                      <span className="info-box-text">
                                        Failed Records
                                      </span>
                                      <span className="info-box-number">
                                        {importResults.failed || 0}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {importResults.errors &&
                                importResults.errors.length > 0 && (
                                  <div>
                                    <h5 className="mb-3 text-danger">
                                      Errors:
                                    </h5>
                                    <div className="table-responsive">
                                      <table className="table table-bordered table-striped">
                                        <thead className="thead-light">
                                          <tr>
                                            <th>Row</th>
                                            <th>Error</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {importResults.errors.map(
                                            (error, index) => (
                                              <tr key={index}>
                                                <td>{error.row}</td>
                                                <td className="text-danger">
                                                  {error.error}
                                                </td>
                                              </tr>
                                            ),
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcurementTrackerAdd;
